import { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';

const GET_GROUPS = gql`
  query GetGroups {
    contactGroups {
      id
      name
      description
      contactCount
      createdAt
    }
  }
`;

const CREATE_GROUP = gql`
  mutation CreateGroup($name: String!, $description: String) {
    createContactGroup(name: $name, description: $description) {
      id
      name
      description
    }
  }
`;

const UPDATE_GROUP = gql`
  mutation UpdateGroup($id: Int!, $name: String, $description: String) {
    updateContactGroup(id: $id, name: $name, description: $description) {
      id
      name
      description
    }
  }
`;

const DELETE_GROUP = gql`
  mutation DeleteGroup($id: Int!) {
    deleteContactGroup(id: $id)
  }
`;

function ContactGroups() {
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });

  const { data, loading, refetch } = useQuery(GET_GROUPS);

  const [createGroup] = useMutation(CREATE_GROUP, {
    onCompleted: () => {
      refetch();
      setShowModal(false);
      setFormData({ name: '', description: '' });
    },
  });

  const [updateGroup] = useMutation(UPDATE_GROUP, {
    onCompleted: () => {
      refetch();
      setShowModal(false);
      setEditingGroup(null);
      setFormData({ name: '', description: '' });
    },
  });

  const [deleteGroup] = useMutation(DELETE_GROUP, {
    onCompleted: () => refetch(),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (editingGroup) {
      await updateGroup({
        variables: {
          id: editingGroup.id,
          name: formData.name,
          description: formData.description,
        },
      });
    } else {
      await createGroup({
        variables: {
          name: formData.name,
          description: formData.description || null,
        },
      });
    }
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setFormData({ name: group.name, description: group.description || '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this group? Contacts will not be deleted.')) {
      await deleteGroup({ variables: { id } });
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingGroup(null);
    setFormData({ name: '', description: '' });
  };

  return (
    <div className="p-4 md:p-10 max-w-[1400px] mx-auto animate-in fade-in duration-700">
      <div className="bg-white rounded-[2rem] md:rounded-[3rem] shadow-sm border border-gray-100 p-6 md:p-12 mb-8 md:mb-12 overflow-hidden relative group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-bulky-blue-500/5 rounded-full -mr-32 -mt-32 blur-3xl transition-transform duration-1000 group-hover:scale-110"></div>
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8 relative z-10">
          <div>
            <div className="flex items-center gap-4 mb-3">
              <div className="w-2 h-8 bg-bulky-blue-500 rounded-full"></div>
              <h2 className="font-medium">Collective Segments</h2>
            </div>
            <p className="text-bulky-navy-400 text-[10px] md:text-xs font-normal uppercase tracking-[0.2em] md:tracking-[0.3em] ml-6 flex flex-wrap items-center gap-3">
              <span className="flex items-center gap-1.5 text-bulky-blue-500">
                <span className="material-icons-round text-sm">hub</span>
                Network Cluster Control
              </span>
              <span className="w-1 h-1 bg-gray-300 rounded-full hidden md:block"></span>
              Organize entities into logical communication matrices
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="w-full lg:w-auto bg-bulky-blue-500 hover:bg-bulky-blue-600 text-white font-normal px-10 py-5 rounded-[1.25rem] transition-all shadow-2xl shadow-bulky-blue-500/25 active:scale-95 flex items-center justify-center gap-3 text-[10px] uppercase tracking-[0.2em]"
          >
            <span className="material-icons-round text-lg">add_circle</span>
            <span>Initialize Segment</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-40 text-center">
          <div className="w-16 h-16 border-4 border-bulky-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-8 shadow-xl shadow-bulky-blue-500/10"></div>
          <p className="text-[10px] font-normal text-bulky-navy-400 uppercase tracking-[0.3em] animate-pulse">Scanning Collective Hierarchy...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
          {data?.contactGroups.map(group => (
            <div
              key={group.id}
              className="group bg-white rounded-[2.5rem] md:rounded-[3rem] border border-gray-100 p-8 md:p-10 shadow-sm hover:shadow-2xl hover:shadow-bulky-blue-500/10 transition-all duration-700 relative overflow-hidden flex flex-col"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-bulky-blue-500/5 rounded-full -mr-12 -mt-12 transition-transform duration-1000 group-hover:scale-150"></div>

              <div className="relative z-10 flex-1 flex flex-col">
                <div className="flex justify-between items-start mb-8 md:mb-12">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-bulky-blue-50 flex items-center justify-center text-bulky-blue-500 shadow-inner group-hover:bg-bulky-blue-500 group-hover:text-white transition-all duration-500 group-hover:rotate-[15deg]">
                    <span className="material-icons-round text-2xl md:text-4xl">hub</span>
                  </div>
                  <div className="flex gap-2 lg:opacity-0 lg:translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                    <button
                      onClick={() => handleEdit(group)}
                      className="w-10 h-10 rounded-xl bg-bulky-blue-50 text-bulky-blue-500 hover:bg-bulky-blue-500 hover:text-white border border-bulky-blue-100/50 transition-all flex items-center justify-center shadow-sm active:scale-90"
                    >
                      <span className="material-icons-round text-lg">edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(group.id)}
                      className="w-10 h-10 rounded-xl bg-red-50 text-red-500 hover:bg-red-500 hover:text-white border border-red-100 transition-all flex items-center justify-center shadow-sm active:scale-90"
                    >
                      <span className="material-icons-round text-lg">delete_sweep</span>
                    </button>
                  </div>
                </div>

                <div className="mb-10 flex-1">
                  <h2 className="font-medium">
                    {group.name}
                  </h2>
                  <p className="text-bulky-navy-500 text-xs md:text-sm font-normal leading-relaxed mb-6 opacity-70 group-hover:opacity-100 transition-opacity line-clamp-3">
                    {group.description || 'No specialized description provided for this collective segment. Identity parameters remain standard within the matrix.'}
                  </p>
                </div>

                <div className="pt-8 md:pt-10 border-t border-gray-50 flex items-center justify-between">
                  <div>
                    <span className="text-[9px] font-normal text-gray-400 uppercase tracking-wider mb-2 block">Entity Capacity</span>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl md:text-4xl font-display font-normal text-bulky-blue-500 leading-none">{group.contactCount}</span>
                      <span className="text-[10px] font-normal text-gray-400 uppercase tracking-wider">Units</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-[9px] font-normal text-gray-400 uppercase tracking-wider mb-2 block">Matrix ID</span>
                    <span className="text-[10px] font-normal text-bulky-blue-500 bg-bulky-blue-50 px-4 py-2 rounded-xl border border-bulky-blue-100/50 tracking-wider shadow-sm">
                      SEG-{group.id.toString().padStart(4, '0')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {data?.contactGroups.length === 0 && (
            <div className="col-span-full py-32 md:py-48 text-center bg-white rounded-[3rem] border-2 border-dashed border-gray-100 px-6 group">
              <div className="w-24 h-24 bg-gray-50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-10 text-gray-200 shadow-inner group-hover:scale-110 transition-transform duration-700">
                <span className="material-icons-round text-6xl">folder_off</span>
              </div>
              <h2 className="font-medium">Segment Matrix Empty</h2>
              <p className="text-gray-400 text-xs md:text-sm font-normal uppercase tracking-[0.2em] mt-2 mb-12 max-w-sm mx-auto leading-relaxed">No collective clusters have been initialized. Database is optimized and awaiting entry.</p>
              <button
                onClick={() => setShowModal(true)}
                className="bg-bulky-navy-800 text-white font-normal px-12 py-5 rounded-[1.25rem] hover:bg-bulky-blue-500 transition-all shadow-2xl shadow-black/10 active:scale-95 text-[10px] uppercase tracking-[0.2em]"
              >
                Launch Primary Segment
              </button>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-bulky-navy-900/60 backdrop-blur-md flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-lg w-full overflow-hidden border border-white/20 animate-in fade-in zoom-in duration-300 max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-br from-bulky-navy-800 to-bulky-navy-950 p-8 md:p-12 text-white relative shrink-0">
              <div className="absolute top-0 right-0 w-32 h-32 bg-bulky-blue-500/10 rounded-bl-full animate-pulse"></div>
              <h2 className="font-medium">
                {editingGroup ? 'Modify Segment' : 'New Cluster'}
              </h2>
              <p className="text-bulky-navy-300 text-[10px] font-normal uppercase tracking-[0.2em] mt-4 relative z-10 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-bulky-blue-500 rounded-full"></span>
                Define segment parameters in the matrix
              </p>
              <button
                onClick={handleCloseModal}
                className="absolute top-8 right-8 w-10 h-10 flex items-center justify-center rounded-[1rem] bg-white/5 text-white/80 hover:text-white hover:bg-white/10 transition-all z-20"
              >
                <span className="material-icons-round text-xl">close</span>
              </button>
            </div>

            <div className="p-8 md:p-12 overflow-y-auto custom-scrollbar">
              <form onSubmit={handleSubmit} className="space-y-10">
                <div className="relative group/input">
                  <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-4 ml-1 flex items-center justify-between">
                    <span>Cluster Designation</span>
                    <span className="text-bulky-blue-500 text-[8px] tracking-[0.3em]">Required</span>
                  </label>
                  <div className="relative">
                    <span className="material-icons-round absolute left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within/input:text-bulky-blue-500 transition-colors">label</span>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full pl-14 pr-6 py-4.5 bg-gray-50 border-2 border-transparent rounded-[1.25rem] focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 shadow-sm"
                      placeholder="e.g., CORE-IDENTITY-POOL"
                      required
                    />
                  </div>
                </div>

                <div className="relative group/input">
                  <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-4 ml-1">
                    Descriptive Metadata
                  </label>
                  <div className="relative">
                    <span className="material-icons-round absolute left-5 top-6 text-gray-300 group-focus-within/input:text-bulky-blue-500 transition-colors">description</span>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full pl-14 pr-6 py-5 bg-gray-50 border-2 border-transparent rounded-[1.25rem] focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 min-h-[160px] resize-none shadow-sm"
                      placeholder="Specify the operational purpose of this cluster segment..."
                    />
                  </div>
                  <p className="mt-4 text-[9px] font-normal text-gray-400 uppercase tracking-wider leading-relaxed ml-1 italic opacity-60">Metadata aids in automated routing and classification protocols.</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 px-8 py-5 border-2 border-gray-100 text-bulky-navy-500 font-normal rounded-[1.25rem] hover:bg-gray-50 transition-all text-[10px] uppercase tracking-wider"
                  >
                    Terminate
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-8 py-5 bg-bulky-blue-500 text-white font-normal rounded-[1.25rem] hover:bg-bulky-blue-600 shadow-2xl shadow-bulky-blue-500/30 active:scale-95 transition-all text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-3"
                  >
                    <span className="material-icons-round text-lg">{editingGroup ? 'sync' : 'publish'}</span>
                    {editingGroup ? 'Apply Mutation' : 'Authorize Segment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactGroups;
